// theme-sources/js/theme/components/cart-discount.js

import { logger } from "../core/logger";
import { THEME_EVENTS, publish } from "../../shared/events";
import { fetchConfig } from "../core/utils";
import { syncCartFakeItem } from "../features/cart-fake-item";

class CartDiscountForm extends HTMLElement {
  constructor() {
    super();

    this.activeRequest = null;

    this.addEventListener("submit", this.onSubmit.bind(this));
    this.addEventListener("click", this.onClick.bind(this));
  }

  get cartDrawer() {
    return document.querySelector("cart-drawer");
  }

  get errorBox() {
    return this.querySelector(".cart-discount__error");
  }

  get errorDiscountCode() {
    return this.querySelector("[data-discount-error-discount-code]");
  }

  get errorShipping() {
    return this.querySelector("[data-discount-error-shipping]");
  }

  get input() {
    return this.querySelector('input[name="discount"]');
  }

  get panel() {
    return this.querySelector("[data-description-tab-panel]");
  }

  getSectionsToRender() {
    const cartDrawer = this.cartDrawer;

    return [
      {
        id: "CartDrawer",
        section: cartDrawer?.dataset.sectionId,
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ].filter((section) => !!section.section);
  }

  getExistingDiscounts() {
    return [...this.querySelectorAll("[data-discount-code]")]
      .map((element) => element.dataset.discountCode)
      .filter(Boolean);
  }

  updatePanelHeight() {
    const panel = this.panel;
    if (!panel) return;

    requestAnimationFrame(() => {
      panel.style.maxHeight = `${panel.scrollHeight}px`;
    });
  }

  showError(type = "discount_code") {
    const discountError = this.errorDiscountCode;
    const shippingError = this.errorShipping;

    [discountError, shippingError].forEach((el) => {
      if (!el) return;
      el.classList.add("opacity-0", "pointer-events-none");
      el.classList.remove("opacity-100");
    });

    const target = type === "shipping" ? shippingError : discountError;

    if (target) {
      target.classList.remove("opacity-0", "pointer-events-none");
      target.classList.add("opacity-100");
    }

    const input = this.input;
    if (input) {
      input.classList.add("border-red-700", "bg-red-50");
      input.classList.remove("border-black/10", "bg-white");
    }

    this.updatePanelHeight();
  }

  hideError() {
    [this.errorDiscountCode, this.errorShipping].forEach((el) => {
      if (!el) return;
      el.classList.add("opacity-0", "pointer-events-none");
      el.classList.remove("opacity-100");
    });

    const input = this.input;
    if (input) {
      input.classList.remove("border-red-700", "bg-red-50");
      input.classList.add("border-black/10", "bg-white");
    }

    this.updatePanelHeight();
  }

  setLoading(isLoading) {
    this.classList.toggle("is-loading", isLoading);
    document.documentElement.classList.toggle("cart-is-updating", isLoading);

    const submitButton = this.querySelector('button[type="submit"]');
    if (submitButton) {
      submitButton.disabled = isLoading;
      submitButton.setAttribute("aria-disabled", isLoading ? "true" : "false");
    }
  }

  abortActiveRequest() {
    if (this.activeRequest) {
      this.activeRequest.abort();
    }

    this.activeRequest = new AbortController();
    return this.activeRequest;
  }

  async updateDiscounts(discounts) {
    const controller = this.abortActiveRequest();
    const sections = this.getSectionsToRender().map(
      (section) => section.section,
    );

    const response = await fetch(`${theme.routes.cart_update_url}`, {
      ...fetchConfig(),
      body: JSON.stringify({
        discount: discounts.join(","),
        sections,
        sections_url: window.location.pathname,
      }),
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`cart_update failed with status ${response.status}`);
    }

    return response.json();
  }

  async normalizeState(parsedState) {
    const sections = this.getSectionsToRender().map(
      (section) => section.section,
    );

    return syncCartFakeItem({
      baseState: parsedState,
      sections,
      sectionsUrl: window.location.pathname,
    });
  }

  renderDrawerState(parsedState) {
    const cartDrawer = this.cartDrawer;
    if (!cartDrawer) {
      logger.warn("CartDiscountForm", "missing cartDrawer");
      return;
    }

    cartDrawer.classList.toggle("is-empty", parsedState.item_count === 0);

    const drawerSectionId = cartDrawer.dataset.sectionId;
    const hasRenderableSections =
      !!parsedState?.sections &&
      !!drawerSectionId &&
      !!parsedState.sections[drawerSectionId] &&
      !!parsedState.sections["cart-icon-bubble"];

    logger.info("CartDiscountForm", "renderDrawerState", {
      hasSections: !!parsedState?.sections,
      sectionKeys: Object.keys(parsedState?.sections || {}),
      hasRenderableSections,
    });

    if (hasRenderableSections) {
      cartDrawer.renderContents(parsedState);
    }

    publish(THEME_EVENTS.cartUpdate, {
      source: "cart-discount-form",
      cartData: parsedState,
    });
  }

  async onSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    event.preventDefault();

    const input = this.input;
    if (!input) return;

    const code = input.value.trim();
    if (!code) return;

    const existingDiscounts = this.getExistingDiscounts();
    const attemptedCode = code.toLowerCase();

    if (
      existingDiscounts.some(
        (existingCode) => existingCode.toLowerCase() === attemptedCode,
      )
    ) {
      this.showError("discount_code");
      return;
    }

    this.hideError();
    this.setLoading(true);

    try {
      const parsedState = await this.updateDiscounts([
        ...existingDiscounts,
        code,
      ]);

      logger.info(
        "CartDiscountForm",
        "apply discount parsedState",
        parsedState,
      );

      const matchingDiscount = (parsedState.discount_codes || []).find(
        (discount) => discount.code?.toLowerCase() === attemptedCode,
      );

      if (matchingDiscount && matchingDiscount.applicable === false) {
        input.value = "";
        this.showError("discount_code");
        return;
      }

      const finalState = await this.normalizeState(parsedState);
      this.renderDrawerState(finalState);
    } catch (error) {
      if (error?.name !== "AbortError") {
        logger.error("CartDiscountForm", "apply discount error", error);
        this.showError("discount_code");
      }
    } finally {
      this.setLoading(false);
    }
  }

  async onClick(event) {
    const removeButton = event.target.closest(
      ".cart-discount__pill-remove, .cart-discount__remove-link",
    );
    if (!removeButton) return;

    event.preventDefault();

    const codeContainer = removeButton.closest("[data-discount-code]");
    if (!codeContainer) return;

    const codeToRemove = codeContainer.dataset.discountCode;
    if (!codeToRemove) return;

    const nextDiscounts = this.getExistingDiscounts().filter(
      (code) => code !== codeToRemove,
    );

    this.hideError();
    this.setLoading(true);

    try {
      const parsedState = await this.updateDiscounts(nextDiscounts);

      logger.info(
        "CartDiscountForm",
        "remove discount parsedState",
        parsedState,
      );

      const finalState = await this.normalizeState(parsedState);
      this.renderDrawerState(finalState);
    } catch (error) {
      if (error?.name !== "AbortError") {
        logger.error("CartDiscountForm", "remove discount error", error);
      }
    } finally {
      this.setLoading(false);
    }
  }
}

if (!customElements.get("cart-discount-form")) {
  customElements.define("cart-discount-form", CartDiscountForm);
}
